<script setup lang="ts">
import { throttle } from 'throttle-debounce'
import type { SliderOverlay } from './types'
import SliderDots from './SliderDots.vue'

const enum Directions {
  Prev = 'prev',
  Next = 'next',
}

type Props = {
  spaceBetween: number
  slideWidth: number
  slidesToScroll?: number
  length?: number
  overlay?: SliderOverlay
  arrows?: boolean
  arrowsOffset?: number | string
  dots?: boolean
  dotsOffset?: number | string
  dotsFixed?: boolean
  isDifferentWidth?: boolean
  snap?: boolean
  slideClass?: string
  slideActiveClass?: string
  scrollTrigger?: string | number
}

const props = withDefaults(defineProps<Props>(), {
  slidesToScroll: 1,
  length: undefined,
  overlay: undefined,
  arrows: true,
  arrowsOffset: -20,
  snap: true,
  slideClass: '',
  slideActiveClass: '',
  scrollTrigger: '',
  dotsOffset: 8,
  dotsFixed: true,
})

const sliderTrackElement = ref<HTMLDivElement | null>(null)

const isDisabledPrev = ref(true)
const isDisabledNext = ref(true)

const sliderTrackScrollWidth = ref(0)
const sliderTrackClientWidth = ref(0)

const slideActiveIndex = ref(0)

const resizeObserver = ref<ResizeObserver | null>(null)

const componentClasses = computed(() => {
  return [
    'app-slider',
    {
      [`app-slider--overlay-${props.overlay}`]: props.overlay,
      'app-slider--snap': props.snap,
    },
  ]
})

const dotsClasses = computed(() => {
  return ['app-slider__dots', { 'app-slider__dots--fixed': props.dotsFixed }]
})

const updateWidth = () => {
  if (!sliderTrackElement.value) return

  const { scrollWidth, clientWidth } = sliderTrackElement.value

  sliderTrackScrollWidth.value = scrollWidth
  sliderTrackClientWidth.value = clientWidth
}

const onScrollTo = (direction: Directions) => {
  if (!sliderTrackElement.value) return

  /*
   * Множитель для управления направлением скролла
   * Назад = меняем координаты на отрицательные
   * Вперёд = меняем координаты на положительные
   * */
  const multiplier = direction === Directions.Prev ? -1 : 1

  // Сдвиг скролла для слайдов с разной шириной
  const offsetWithDifferentWidth =
    sliderTrackClientWidth.value * 0.75 * multiplier

  // Сдвиг скролла для слайдов с одинаковой шириной
  const offsetWithSameWidth =
    (props.slideWidth + props.spaceBetween) * props.slidesToScroll * multiplier

  const offset = props.isDifferentWidth
    ? offsetWithDifferentWidth
    : offsetWithSameWidth

  scrollTo(sliderTrackElement.value.scrollLeft + offset)
}

const setSlideActiveIndex = (scrollLeft: number) => {
  slideActiveIndex.value = Math.round(
    scrollLeft / (sliderTrackClientWidth.value + props.spaceBetween),
  )
}

const scrollEventListener = throttle(100, () => {
  // Сдвиг скролла берущийся из значений в DOM
  const scrollLeft = Math.ceil(sliderTrackElement?.value?.scrollLeft || 0)

  const scrollWidth = sliderTrackElement?.value?.scrollWidth || 0

  // Скрываем кнопку "Назад" когда сдвиг скролла нулевой
  isDisabledPrev.value = scrollLeft <= 0

  // Скрываем кнопку "Вперёд" когда скролл дошёл до правого края
  isDisabledNext.value =
    scrollLeft >= scrollWidth - sliderTrackClientWidth.value

  setSlideActiveIndex(scrollLeft)
})

const clickEventListener = (event: Event) => {
  // Определяем целевой элемент по которому произошёл клик
  const eventTarget = event.target as HTMLElement

  // Находим ближайший родительский элемент с классом слайда
  const element = eventTarget.closest(`.${props.slideClass}`) as HTMLElement

  if (!element) return

  scrollTo(getElementOffset(element))
}

const scrollToActiveSlide = async () => {
  if (!props.slideActiveClass) return

  await nextTick()

  // Находим элемент слайда с активным классом
  const activeElement = sliderTrackElement.value?.querySelector(
    `.${props.slideActiveClass}`,
  ) as HTMLElement

  if (!activeElement) return

  scrollTo(getElementOffset(activeElement), 'instant')
}

const scrollTo = (left?: number, behavior: ScrollBehavior = 'smooth') => {
  sliderTrackElement.value?.scrollTo({ left, behavior })
}

// Вычисление сдвига активного элемента
const getElementOffset = (element: HTMLElement) => {
  const { offsetLeft, clientWidth } = element

  return offsetLeft + clientWidth / 2 - sliderTrackClientWidth.value / 2
}

const checkNextArrow = () => {
  // Скрываем кнопку "Вперёд", когда скролл не требуется и оба контейнера одинаковой длины
  isDisabledNext.value =
    sliderTrackScrollWidth.value === sliderTrackClientWidth.value
}

const initSlider = () => {
  setTimeout(() => {
    updateWidth()
    checkNextArrow()
  }, 100)
}

const attachObserver = () => {
  resizeObserver.value = new ResizeObserver(() => {
    updateWidth()
    checkNextArrow()
  })

  if (resizeObserver.value && sliderTrackElement.value) {
    resizeObserver.value.observe(sliderTrackElement.value)
  }
}

const detachObserver = () => {
  if (resizeObserver.value && sliderTrackElement.value) {
    resizeObserver.value.unobserve(sliderTrackElement.value)
  }
}

watch(
  () => props.scrollTrigger,
  (value, oldValue) => {
    if (value !== oldValue) scrollToActiveSlide()
  },
)

watch(
  () => props.length,
  () => initSlider(),
)

onMounted(() => {
  initSlider()

  sliderTrackElement.value?.addEventListener('scroll', scrollEventListener)

  if (props.slideClass) {
    sliderTrackElement.value?.addEventListener('click', clickEventListener)
  }

  attachObserver()

  setTimeout(() => {
    if (props.slideActiveClass) scrollToActiveSlide()
  }, 110)
})

onBeforeUnmount(() => {
  sliderTrackElement.value?.removeEventListener('scroll', scrollEventListener)
  sliderTrackElement.value?.removeEventListener('click', clickEventListener)

  detachObserver()
})
</script>

<template>
  <div data-test-id="slider" :class="componentClasses">
    <template v-if="overlay">
      <div
        data-test-id="slider-overlay-prev"
        class="app-slider__overlay app-slider__overlay--prev"
        :class="{ 'app-slider__overlay--hidden': isDisabledPrev }"
      />
      <div
        data-test-id="slider-overlay-next"
        class="app-slider__overlay app-slider__overlay--next"
        :class="{ 'app-slider__overlay--hidden': isDisabledNext }"
      />
    </template>

    <template v-if="arrows">
      <the-button
        data-test-id="slider-button-prev"
        class="app-slider__button"
        :class="{ 'app-slider__button--disabled': isDisabledPrev }"
        :style="{ left: `${arrowsOffset}px` }"
        variant="white"
        color="secondary"
        icon="expand-left"
        size="sm"
        radius="lg"
        aria-label="Предыдущий слайд"
        type="button"
        @click="onScrollTo(Directions.Prev)"
      />

      <the-button
        data-test-id="slider-button-next"
        class="app-slider__button"
        :class="{ 'app-slider__button--disabled': isDisabledNext }"
        :style="{ right: `${arrowsOffset}px` }"
        variant="white"
        color="secondary"
        icon="expand-right"
        size="sm"
        radius="lg"
        aria-label="Следующий слайд"
        type="button"
        @click="onScrollTo(Directions.Next)"
      />
    </template>

    <div class="app-slider__wrapper">
      <div
        ref="sliderTrackElement"
        data-test-id="slider-track"
        class="app-slider__track"
        :style="{ gap: `${spaceBetween}px` }"
      >
        <slot />
      </div>

      <SliderDots
        v-if="dots && length > 0"
        :class="dotsClasses"
        :style="{ paddingTop: `${dotsOffset}px` }"
        :length="length"
        :active-index="slideActiveIndex"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '../../assets/ui-layer/styles/mixins' as *;

.app-slider {
  $gradient: linear-gradient(
    -90deg,
    var(--ui-color-white) 59.06%,
    rgba(var(--ui-color-white-rgb), 0) 100%
  );

  position: relative;
  z-index: 5;
  width: 100%;

  &__overlay {
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 4;
    width: var(--slider-overlay-width);
    pointer-events: none;
    transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;

    &--hidden {
      pointer-events: none;
      visibility: hidden;
      opacity: 0;
    }

    &--prev {
      left: 0;
      background: $gradient;
      transform: scale(-1);
    }

    &--next {
      right: 0;
      background: $gradient;
    }
  }

  &__button {
    position: absolute !important;
    top: 50%;
    z-index: 5;
    box-shadow: var(--ui-shadow-bottom);
    transform: translateY(-50%);

    &--disabled {
      pointer-events: none;
      visibility: hidden;
      opacity: 0;
    }
  }

  &__wrapper {
    width: 100%;
    overflow: hidden;
  }

  &__track {
    display: flex;
    overflow-x: auto;
    scroll-behavior: smooth;
    @include invisible-scroll-bar();

    & > :deep(*) {
      flex-shrink: 0;
    }
  }

  &__dots {
    &--fixed {
      position: absolute;
      top: 100%;
      right: 0;
      left: 0;
    }
  }

  /* Modifiers */
  &--overlay-sm {
    --slider-overlay-width: 44px;
  }

  &--overlay-lg {
    --slider-overlay-width: 64px;
  }

  &--snap {
    .app-slider__track {
      -ms-scroll-snap-type: x mandatory;
      scroll-snap-type: x mandatory;

      & > :deep(*) {
        scroll-snap-align: start;
        scroll-snap-stop: always;
      }
    }
  }
}
</style>
